import type { CrudFilter } from "@refinedev/core";

/**
 * El filtro "Match con el catálogo" de Productos asociados (xcons-docs#624). El % está guardado,
 * así que se filtra por tramos sin calcular nada: los tres del semáforo y un rango a mano. Todos
 * viajan como `filter.matchScore`. Un producto sin nada parecido en el catálogo tiene 0% y entra
 * en el rojo: ya no hay un tramo "sin sugerencia" (xcons-docs#631).
 */
export type TramoDeMatch = "verde" | "amarillo" | "rojo" | "personalizado";

/** Los extremos de cada tramo, de 0 a 1 e incluidos: la API guarda el % con cuatro decimales. */
const RANGOS: Record<"verde" | "amarillo" | "rojo", [number, number]> = {
  verde: [0.8, 1],
  amarillo: [0.5, 0.7999],
  rojo: [0, 0.4999],
};

export const opcionesDeMatch = (t: any) => [
  { value: "verde", label: t("filter.products.matchGreen") },
  { value: "amarillo", label: t("filter.products.matchYellow") },
  { value: "rojo", label: t("filter.products.matchRed") },
  { value: "personalizado", label: t("filter.products.matchCustom") },
];

/**
 * Los filtros que manda el formulario: el tramo, o desde/hasta en % si es personalizado. Van
 * **siempre todos los operadores**, los que no se usan vacíos: refine mezcla los filtros por campo y
 * operador, y sin eso un `eq` viejo ("sin sugerencia", que ya no existe) quedaría junto al tramo.
 */
export const filtrosDeMatch = (
  tramo?: TramoDeMatch,
  desde?: number,
  hasta?: number,
): CrudFilter[] => {
  let rango: [number, number] | undefined;
  if (tramo === "personalizado" && (desde !== undefined || hasta !== undefined)) {
    rango = [(desde ?? 0) / 100, (hasta ?? 100) / 100];
  } else if (tramo && tramo !== "personalizado") {
    rango = RANGOS[tramo];
  }
  return [
    { field: "matchScore", operator: "eq", value: undefined },
    { field: "matchScore", operator: "between", value: rango },
    // Un "desde" suelto (`gte`) que haya llegado por la URL: elegir un tramo lo reemplaza.
    { field: "matchScore", operator: "gte", value: undefined },
  ];
};

/** De vuelta: el tramo y el rango del filtro aplicado, para el formulario. */
export const tramoDelFiltro = (
  value: unknown,
): { tramo?: TramoDeMatch; desde?: number; hasta?: number } => {
  // Un número suelto es un "desde" (`$gte`, como en un link armado a mano): hasta el 100%.
  const rango = Array.isArray(value) ? value : [value, 1];
  const [d, h] = rango.map(Number);
  if (rango.length !== 2 || !Number.isFinite(d) || !Number.isFinite(h)) return {};
  const tramo = (Object.keys(RANGOS) as Array<keyof typeof RANGOS>).find(
    (k) => RANGOS[k][0] === d && RANGOS[k][1] === h,
  );
  return tramo
    ? { tramo }
    : { tramo: "personalizado", desde: Math.round(d * 100), hasta: Math.round(h * 100) };
};

/** Lo que dice el tag del filtro aplicado. */
export const etiquetaDelFiltroDeMatch = (t: any, value: unknown): string => {
  const { tramo, desde, hasta } = tramoDelFiltro(value);
  if (!tramo) return String(value);
  if (tramo === "personalizado") return `${desde}% a ${hasta}%`;
  return opcionesDeMatch(t).find((o) => o.value === tramo)!.label;
};
